<template>
  <v-bottom-sheet v-model="showSheet" scrollable max-width="100%" swipeable class="planner-action-sheet-container">
    <v-card
      class="planner-action-sheet"
      @touchstart="handleTouchStart"
      @touchmove="handleTouchMove"
      @touchend="handleTouchEnd"
    >
      <!-- Swipe Handle -->
      <div class="swipe-handle-bar" @click="close">
        <div class="handle-bar"></div>
      </div>

      <!-- Header -->
      <v-card-title class="d-flex align-center pa-4 pb-2 pt-0">
        <div class="flex-grow-1">
          <div class="text-overline grey--text mb-0">{{ plannerTypeLabel }}</div>
          <div class="text-h6">{{ planner.plannerName }}</div>
        </div>
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="pa-0 action-sheet-content">
        <!-- Loading State -->
        <div v-if="loading" class="d-flex justify-center align-center py-8">
          <v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
        </div>

        <v-list v-else class="py-0">
          <!-- ACTIVE MODE: Show Start New + In-Progress -->
          <template v-if="mode === 'active'">
            <!-- Start New Planner -->
            <v-list-item link class="action-item action-item--primary" @click="handleStartNew">
              <v-list-item-icon class="mr-4">
                <v-icon color="primary" large>mdi-plus-circle</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="text-subtitle-1 font-weight-medium">
                  {{ $t("t_start_new_planner") }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  {{ $t("t_start_new_planner_desc") }}
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-icon>mdi-chevron-right</v-icon>
              </v-list-item-action>
            </v-list-item>

            <!-- In-Progress Submissions -->
            <template v-if="inProgressSubmissions.length > 0">
              <v-divider></v-divider>
              <div class="action-section-header overline px-4 pt-3 pb-1 grey--text">
                <v-icon x-small class="mr-1">mdi-progress-clock</v-icon>
                {{ $t("t_in_progress") }} ({{ inProgressSubmissions.length }})
              </div>

              <v-list-item
                v-for="submission in inProgressSubmissions"
                :key="submission.id"
                link
                class="action-item"
                @click="handleSelect(submission)"
              >
                <v-list-item-icon class="mr-4">
                  <v-progress-circular
                    :value="submission.completionPercentage || 0"
                    :color="getProgressColor(submission.completionPercentage)"
                    size="40"
                    width="4"
                  >
                    <span class="text-caption">{{ submission.completionPercentage || 0 }}%</span>
                  </v-progress-circular>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title class="text-body-1">
                    {{ $t("t_continue") }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    {{ $t("t_started") }}: {{ formatDateTimeContextual(submission.createdOn || submission.updatedOn) }}
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-icon color="primary">mdi-play-circle</v-icon>
                </v-list-item-action>
              </v-list-item>
            </template>

            <!-- Empty state for active -->
            <template v-if="inProgressSubmissions.length === 0">
              <v-divider></v-divider>
              <div class="text-center py-4 grey--text">
                <v-icon class="mb-2" color="grey lighten-1">mdi-progress-clock</v-icon>
                <div class="text-caption">{{ $t("t_no_active_submissions") }}</div>
              </div>
            </template>
          </template>

          <!-- HISTORY MODE: Show Completed Submissions -->
          <template v-if="mode === 'history'">
            <template v-if="completedSubmissions.length > 0">
              <div class="action-section-header overline px-4 pt-3 pb-1 grey--text">
                <v-icon x-small class="mr-1" color="success">mdi-check-circle</v-icon>
                {{ $t("t_completed") }} ({{ completedSubmissions.length }})
              </div>

              <v-list-item
                v-for="submission in displayedCompletedSubmissions"
                :key="submission.id"
                link
                class="action-item"
                @click="handleSelect(submission)"
              >
                <v-list-item-icon class="mr-4">
                  <v-icon color="success">mdi-file-check</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title class="text-body-2">
                    {{ formatDateTime(submission.completedOn) }}
                  </v-list-item-title>
                  <v-list-item-subtitle v-if="submission.submittedBy">
                    {{ submission.submittedBy }}
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-icon small>mdi-eye-outline</v-icon>
                </v-list-item-action>
              </v-list-item>

              <!-- Show more completed -->
              <v-list-item
                v-if="completedSubmissions.length > pageSize && !showAllCompleted"
                link
                class="action-item action-item--more"
                @click="showAllCompleted = true"
              >
                <v-list-item-icon class="mr-4">
                  <v-icon>mdi-dots-horizontal</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title class="text-body-2 grey--text">
                    {{ $t("t_view_all") }} ({{ completedSubmissions.length }})
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </template>

            <!-- Empty state for history -->
            <template v-else>
              <div class="text-center py-6 grey--text">
                <v-icon class="mb-2" size="48" color="grey lighten-1">mdi-file-document-outline</v-icon>
                <div class="text-body-2">{{ $t("t_no_submissions") }}</div>
              </div>
            </template>
          </template>
        </v-list>
      </v-card-text>

      <!-- Safe area padding for mobile -->
      <div class="safe-area-bottom"></div>
    </v-card>
  </v-bottom-sheet>
</template>

<script>
import { PlannerService } from "@services";
import helpers from "@/mixins/helpers";

export default {
  name: "PlannerActionMenu",

  mixins: [helpers],

  props: {
    show: {
      type: Boolean,
      default: false,
    },
    planner: {
      type: Object,
      required: true,
    },
    mode: {
      type: String,
      default: "history", // 'active' or 'history'
      validator: (v) => ["active", "history"].includes(v),
    },
  },

  data() {
    return {
      loading: false,
      submissions: [],
      showAllCompleted: false,
      pageSize: 5,
      touchStart: null,
      touchY: null,
      isDragging: false,
    };
  },

  computed: {
    showSheet: {
      get() {
        return this.show;
      },
      set(val) {
        if (!val) this.$emit("close");
      },
    },

    plannerTypeLabel() {
      const type = this.planner?.plannerType;
      return type ? this.$t(`t_planner_type_${type}`) : this.$t("t_planners");
    },

    inProgressSubmissions() {
      return this.submissions.filter((s) => s.status === 0 || s.status === 1);
    },

    completedSubmissions() {
      return this.submissions.filter((s) => s.status === 2);
    },

    displayedCompletedSubmissions() {
      if (this.showAllCompleted) return this.completedSubmissions;
      return this.completedSubmissions.slice(0, this.pageSize);
    },
  },

  methods: {
    close() {
      this.$emit("close");
    },

    async loadSubmissions() {
      this.loading = true;
      try {
        const params = {
          plannerId: this.planner.plannerId,
          "paging.orderByField": "id",
          "paging.ascending": false,
          "paging.skipNum": 0,
          "paging.takeNum": 50,
        };
        const response = await PlannerService.searchPlannerSubmissions(params);
        this.submissions = response.data || [];
      } catch (error) {
        console.error("Error loading submissions:", error);
        this.submissions = [];
      } finally {
        this.loading = false;
      }
    },

    handleStartNew() {
      this.$emit("start-new", this.planner);
      this.close();
    },

    handleSelect(submission) {
      this.$emit("select-submission", this.planner, submission);
      this.close();
    },

    getProgressColor(percentage) {
      if (percentage >= 80) return "success";
      if (percentage >= 50) return "info";
      if (percentage >= 25) return "warning";
      return "grey";
    },

    handleTouchStart(event) {
      this.touchStart = event.touches[0].clientY;
      this.isDragging = true;
    },

    handleTouchMove(event) {
      if (!this.isDragging) return;

      this.touchY = event.touches[0].clientY;
      const deltaY = this.touchY - this.touchStart;

      if (deltaY > 0) {
        const bottomSheet = event.target.closest(".v-bottom-sheet");
        if (bottomSheet) {
          bottomSheet.style.transform = `translateY(${deltaY}px)`;
          bottomSheet.style.transition = "none";
        }
      }
    },

    handleTouchEnd(event) {
      if (!this.isDragging) return;

      const deltaY = this.touchY - this.touchStart;
      const bottomSheet = event.target.closest(".v-bottom-sheet");

      if (deltaY > 100) {
        this.close();
      }

      if (bottomSheet) {
        bottomSheet.style.transform = "";
        bottomSheet.style.transition = "transform 0.3s";
      }

      this.isDragging = false;
      this.touchStart = null;
      this.touchY = null;
    },
  },

  watch: {
    show: {
      immediate: true,
      handler(val) {
        if (val) {
          this.showAllCompleted = false;
          this.loadSubmissions();
        }
      },
    },
  },

  emits: ["close", "start-new", "select-submission"],
};
</script>

<style lang="scss">
// Force full width at all screen sizes
.planner-action-sheet-container .v-bottom-sheet {
  max-width: 100% !important;
  width: 100% !important;
  left: 0 !important;
  right: 0 !important;
}

.planner-action-sheet {
  border-radius: 0 !important;
  max-height: 80vh;
  overflow-y: auto;
  @media (min-width: 601px) {
    margin-bottom: -55px;
  }
}

.action-sheet-content {
  min-height: 200px;
}

.action-item {
  min-height: 64px;

  &--primary {
    background: rgba(var(--v-primary-base), 0.04);
  }

  &--more {
    min-height: 48px;
  }
}

.action-section-header {
  font-weight: 500;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
}

.safe-area-bottom {
  // Only add padding on mobile devices
  padding-bottom: 0px;

  @media (max-width: 600px) {
    padding-bottom: env(safe-area-inset-bottom, 16px);
  }
}

.swipe-handle-bar {
  position: relative;
  padding: 8px;
  cursor: pointer;

  .handle-bar {
    width: 36px;
    height: 4px;
    border-radius: 2px;
    background-color: rgba(0, 0, 0, 0.2);
    margin: 0 auto;

    &:hover {
      background-color: rgba(0, 0, 0, 0.3);
    }
  }
}

// Dark theme support
.theme--dark {
  .swipe-handle-bar .handle-bar {
    background-color: rgba(255, 255, 255, 0.2);

    &:hover {
      background-color: rgba(255, 255, 255, 0.3);
    }
  }
}
</style>
